import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { REFERENCE_LIBRARY_LIST_COLUMNS, } from '../components/reference/referenceTypes';
import { pruneAuthorCache } from '../components/reference/referenceAuthorCache';
import { releaseBodyHtml } from '../utils/memoryManager';
import axiosInstance from '../utils/axios-auth';
import { supabase } from '../utils/supabase-client';
import { fetchUserBookmarkKeys, toggleUserBookmark } from '../utils/user-bookmarks';
const initialState = {
    library: [],
    libraryStatus: 'idle',
    libraryError: null,
    libraryHasMore: false,
    bookmarkedIds: [],
    bookmarksStatus: 'idle',
    activeDetail: null,
    detailStatus: 'idle',
    detailError: null,
    pendingDetailId: null,
    annotationsByRef: {},
    commentsByRef: {},
    sectionMarksByRef: {},
    bodyHtmlByRef: {},
    bodyShaByRef: {},
    viewerCacheOrder: [],
    annotationsFetchGenByRef: {},
    ui: { search: '', typeFilter: 'all', genreFilter: 'all', bookmarkedOnly: false },
};
function clearViewerSessionState(state) {
    // Clear annotation/comment state so signed-out viewers cannot paint private
    // marks. Keep bodyHtmlByRef — dropping multi‑MB HTML synchronously on logout
    // caused main-thread freezes; body is LRU-evicted separately.
    state.annotationsByRef = {};
    state.commentsByRef = {};
    state.sectionMarksByRef = {};
    state.annotationsFetchGenByRef = {};
    state.activeDetail = null;
    state.detailStatus = 'idle';
    state.detailError = null;
    state.pendingDetailId = null;
    pruneAuthorsForViewerCache(state);
}
/**
 * Merge a server annotation list with any local-only rows that were inserted
 * after the fetch started (or optimistic `temp-*` rows). Prevents a slow
 * in-flight fetch from wiping a highlight the user just saved.
 */
function mergeFetchedAnnotations(existing, fetched, requestedAtIso) {
    const fetchedIds = new Set(fetched.map((r) => r.id));
    const requestedAtMs = Date.parse(requestedAtIso);
    const localKeep = existing.filter((row) => {
        if (fetchedIds.has(row.id))
            return false;
        if (row.id.startsWith('temp-'))
            return true;
        if (!Number.isFinite(requestedAtMs))
            return false;
        return Date.parse(row.created_at) >= requestedAtMs;
    });
    if (localKeep.length === 0)
        return fetched;
    return [...fetched, ...localKeep].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}
export const VIEWER_CACHE_MAX = 3;
function collectViewerAuthorIds(state) {
    const ids = new Set();
    for (const refId of state.viewerCacheOrder) {
        for (const a of state.annotationsByRef[refId] ?? []) {
            if (a.user_id)
                ids.add(a.user_id);
        }
        for (const c of state.commentsByRef[refId] ?? []) {
            if (c.user_id)
                ids.add(c.user_id);
        }
    }
    return ids;
}
function pruneAuthorsForViewerCache(state) {
    pruneAuthorCache(collectViewerAuthorIds(state));
}
function purgeViewerPayload(state, referenceId) {
    delete state.annotationsByRef[referenceId];
    delete state.commentsByRef[referenceId];
    delete state.sectionMarksByRef[referenceId];
    delete state.bodyHtmlByRef[referenceId];
    delete state.bodyShaByRef[referenceId];
    delete state.annotationsFetchGenByRef[referenceId];
    releaseBodyHtml(referenceId);
    pruneAuthorsForViewerCache(state);
}
function evictViewerCacheEntry(state, referenceId) {
    purgeViewerPayload(state, referenceId);
    state.viewerCacheOrder = state.viewerCacheOrder.filter((x) => x !== referenceId);
}
function evictOldestViewerCacheEntries(state, max = VIEWER_CACHE_MAX) {
    while (state.viewerCacheOrder.length > max) {
        const oldest = state.viewerCacheOrder.shift();
        if (!oldest)
            break;
        purgeViewerPayload(state, oldest);
    }
}
/** Register a reference in the viewer LRU and evict oldest entries when over capacity. */
function touchViewerCacheOrder(state, referenceId) {
    if (!referenceId)
        return;
    const order = state.viewerCacheOrder.filter((x) => x !== referenceId);
    order.push(referenceId);
    state.viewerCacheOrder = order;
    evictOldestViewerCacheEntries(state);
}
function applyClientLibraryFilters(rows, opts) {
    let out = rows;
    const tf = opts.typeFilter ?? 'all';
    if (tf !== 'all') {
        out = out.filter((r) => r.type === tf);
    }
    const gf = opts.genreFilter ?? 'all';
    if (gf !== 'all' && (tf === 'all' || tf === 'book')) {
        out = out.filter((r) => {
            if (r.type !== 'book')
                return false;
            const g = r.genre;
            if (g === gf)
                return true;
            if (!g && gf === 'other')
                return true;
            return false;
        });
    }
    if (opts.bookmarkedOnly) {
        const ids = new Set(opts.bookmarkedIds ?? []);
        out = out.filter((r) => ids.has(r.id));
    }
    return out;
}
export const fetchReferencesLibrary = createAsyncThunk('references/fetchLibrary', async (opts = {}, { rejectWithValue }) => {
    const lim = opts.limit ?? 40;
    const offset = opts.offset ?? 0;
    const q = opts.search?.trim();
    const bookmarkedOnly = !!opts.bookmarkedOnly;
    const bookmarkedIds = opts.bookmarkedIds ?? [];
    try {
        if (bookmarkedOnly && !bookmarkedIds.length) {
            return [];
        }
        if (q && q.length >= 2) {
            const { data: rows, error: rpcErr } = await supabase.rpc('search_references', {
                p_query: q,
                p_limit: lim,
            });
            if (rpcErr)
                return rejectWithValue(rpcErr.message);
            const ids = [...new Set(rows?.map((r) => r.reference_id).filter(Boolean) ?? [])];
            if (!ids.length)
                return [];
            const { data: full, error: selErr } = await supabase
                .from('ref_references')
                .select(REFERENCE_LIBRARY_LIST_COLUMNS)
                .in('id', ids)
                .eq('status', 'active')
                .order('updated_at', { ascending: false });
            if (selErr)
                return rejectWithValue(selErr.message);
            const filtered = applyClientLibraryFilters((full ?? []), opts);
            return filtered;
        }
        let query = supabase
            .from('ref_references')
            .select(REFERENCE_LIBRARY_LIST_COLUMNS)
            .eq('status', 'active')
            .order('updated_at', { ascending: false })
            .range(offset, offset + lim - 1);
        const tf = opts.typeFilter ?? 'all';
        if (tf !== 'all')
            query = query.eq('type', tf);
        const gf = opts.genreFilter ?? 'all';
        if (gf !== 'all' && (tf === 'all' || tf === 'book')) {
            query = query.eq('genre', gf);
        }
        if (bookmarkedOnly)
            query = query.in('id', bookmarkedIds);
        const { data, error } = await query;
        if (error)
            return rejectWithValue(error.message);
        return (data ?? []);
    }
    catch (e) {
        return rejectWithValue(e instanceof Error ? e.message : String(e));
    }
});
export const fetchReferenceBookmarks = createAsyncThunk('references/fetchBookmarks', async (_void, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    try {
        return await fetchUserBookmarkKeys({
            table: 'ref_bookmarks',
            keyColumn: 'reference_id',
            userId: uid,
        });
    }
    catch (e) {
        return rejectWithValue(e instanceof Error ? e.message : String(e));
    }
});
export const toggleReferenceBookmark = createAsyncThunk('references/toggleBookmark', async (referenceId, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const currentlyBookmarked = (state.references?.bookmarkedIds ?? []).includes(referenceId);
    try {
        const result = await toggleUserBookmark({
            table: 'ref_bookmarks',
            keyColumn: 'reference_id',
            userId: uid,
            key: referenceId,
            currentlyBookmarked,
        });
        return { referenceId: result.key, bookmarked: result.bookmarked };
    }
    catch (e) {
        return rejectWithValue(e instanceof Error ? e.message : String(e));
    }
});
export const fetchSectionMarks = createAsyncThunk('references/fetchSectionMarks', async (referenceId, { rejectWithValue }) => {
    const { data, error } = await supabase
        .from('ref_section_marks')
        .select('*')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: false });
    if (error)
        return rejectWithValue(error.message);
    return { referenceId, rows: (data ?? []) };
});
export const insertSectionMark = createAsyncThunk('references/insertSectionMark', async (input, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const { data, error } = await supabase
        .from('ref_section_marks')
        .insert([
        {
            user_id: uid,
            reference_id: input.reference_id,
            label: input.label.trim() || 'Bookmark',
            section_id: input.section_id,
            char_offset: Math.max(0, Math.floor(input.char_offset)),
            content_sha256: input.content_sha256 ?? null,
        },
    ])
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    return data;
});
export const deleteSectionMark = createAsyncThunk('references/deleteSectionMark', async (input, { rejectWithValue }) => {
    const { error } = await supabase.from('ref_section_marks').delete().eq('id', input.id);
    if (error)
        return rejectWithValue(error.message);
    return input;
});
export const fetchReferenceDetail = createAsyncThunk('references/fetchDetail', async (referenceId, { rejectWithValue, getState }) => {
    const state = getState();
    const cachedBody = state.references.bodyHtmlByRef[referenceId];
    const columns = cachedBody
        ? REFERENCE_LIBRARY_LIST_COLUMNS
        : `${REFERENCE_LIBRARY_LIST_COLUMNS}, body_html`;
    const { data, error } = await supabase
        .from('ref_references')
        .select(columns)
        .eq('id', referenceId)
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    const row = data;
    if (row && cachedBody) {
        return { ...row, body_html: cachedBody };
    }
    return row;
});
export const fetchAnnotationsForReference = createAsyncThunk('references/fetchAnnotations', async (arg, { rejectWithValue, getState }) => {
    const referenceId = typeof arg === 'string' ? arg : arg.referenceId;
    const requestedAt = new Date().toISOString();
    // pending already bumped the generation; capture ours so a slower older
    // response can be ignored when a newer fetch has started.
    const state = getState();
    const fetchGen = state.references?.annotationsFetchGenByRef?.[referenceId] ?? 0;
    const { data, error } = await supabase
        .from('ref_annotations')
        .select('*')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: true });
    if (error)
        return rejectWithValue(error.message);
    return {
        referenceId,
        rows: (data ?? []),
        requestedAt,
        fetchGen,
    };
});
export const fetchCommentsForReference = createAsyncThunk('references/fetchComments', async (referenceId, { rejectWithValue }) => {
    const { data, error } = await supabase
        .from('ref_comments')
        .select('*')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: true });
    if (error)
        return rejectWithValue(error.message);
    return { referenceId, rows: (data ?? []) };
});
export const insertAnnotation = createAsyncThunk('references/insertAnnotation', async (row, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const { data, error } = await supabase
        .from('ref_annotations')
        .insert([
        {
            reference_id: row.reference_id,
            user_id: uid,
            type: row.type,
            color: row.color ?? '#FFEB3B',
            note_text: row.note_text ?? null,
            anchor_type: row.anchor_type,
            anchor_data: row.anchor_data,
            page_number: row.page_number ?? null,
            start_seconds: row.start_seconds ?? null,
            end_seconds: row.end_seconds ?? null,
            visibility: row.visibility ?? 'private',
            context: row.context ?? null,
        },
    ])
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    if (!data)
        return rejectWithValue('Annotation insert returned no row');
    return {
        row: data,
        clientTempId: row.clientTempId ?? null,
    };
});
export const updateAnnotationVisibility = createAsyncThunk('references/updateAnnotationVisibility', async (input, { rejectWithValue }) => {
    const { data, error } = await supabase
        .from('ref_annotations')
        .update({ visibility: input.visibility })
        .eq('id', input.id)
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    if (!data)
        return rejectWithValue('Annotation not found');
    return data;
});
export const updateAnnotationNote = createAsyncThunk('references/updateAnnotationNote', async (input, { rejectWithValue }) => {
    const trimmed = input.noteText?.trim() || null;
    const { data, error } = await supabase
        .from('ref_annotations')
        // A highlight with note text is semantically a note; clearing the text
        // demotes it back to a plain highlight. Keeps the side-rail type label
        // honest without a separate write.
        .update({ note_text: trimmed, type: trimmed ? 'note' : 'highlight' })
        .eq('id', input.id)
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    if (!data)
        return rejectWithValue('Annotation not found');
    return data;
});
export const deleteAnnotation = createAsyncThunk('references/deleteAnnotation', async (input, { rejectWithValue }) => {
    const { error } = await supabase.from('ref_annotations').delete().eq('id', input.id);
    if (error)
        return rejectWithValue(error.message);
    return input;
});
export const insertComment = createAsyncThunk('references/insertComment', async (input, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const { data, error } = await supabase
        .from('ref_comments')
        .insert([
        {
            reference_id: input.reference_id,
            annotation_id: input.annotation_id,
            parent_id: input.parent_id,
            body: input.body.trim(),
            user_id: uid,
        },
    ])
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    return data;
});
export const insertReferenceRow = createAsyncThunk('references/insertReference', async (row, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const isPlatform = !!row.is_platform;
    const { data, error } = await supabase
        .from('ref_references')
        .insert([
        {
            owner_id: isPlatform ? null : (row.owner_id ?? uid),
            type: row.type,
            title: row.title.trim(),
            author_name: row.author_name ?? null,
            description: row.description ?? null,
            cover_url: row.cover_url ?? null,
            r2_object_key: row.r2_object_key ?? null,
            file_size_bytes: row.file_size_bytes ?? null,
            mime_type: row.mime_type ?? null,
            page_count: row.page_count ?? null,
            duration_seconds: row.duration_seconds ?? null,
            embed_url: row.embed_url ?? null,
            body_html: row.body_html ?? null,
            tags: row.tags ?? [],
            is_platform: isPlatform,
            status: row.status ?? 'active',
        },
    ])
        .select(REFERENCE_LIBRARY_LIST_COLUMNS)
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    return data;
});
export const updateComment = createAsyncThunk('references/updateComment', async (input, { rejectWithValue }) => {
    const trimmed = input.body.trim();
    if (!trimmed)
        return rejectWithValue('Comment cannot be empty');
    const { data, error } = await supabase
        .from('ref_comments')
        .update({ body: trimmed, is_edited: true })
        .eq('id', input.id)
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    return { referenceId: input.referenceId, row: data };
});
export const insertShareRow = createAsyncThunk('references/insertShare', async (input, { rejectWithValue, getState }) => {
    const state = getState();
    const uid = state.auth?.user?.id;
    if (!uid)
        return rejectWithValue('Not signed in');
    const { shared_with_user_id, shared_with_classroom_id } = input;
    if ((!shared_with_user_id && !shared_with_classroom_id) || (shared_with_user_id && shared_with_classroom_id)) {
        return rejectWithValue('Pick exactly one of student or classroom');
    }
    const { data, error } = await supabase
        .from('ref_shares')
        .insert([
        {
            reference_id: input.reference_id,
            shared_by: uid,
            shared_with_user_id: shared_with_user_id ?? null,
            shared_with_classroom_id: shared_with_classroom_id ?? null,
            permission: input.permission,
        },
    ])
        .select('*')
        .maybeSingle();
    if (error)
        return rejectWithValue(error.message);
    return data;
});
export const finalizeReferenceUpload = createAsyncThunk('references/finalizeUpload', async (body, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.post('/reference-library/finalize-upload', body);
        if (!res.data?.success) {
            return rejectWithValue(res.data?.message || 'Finalize failed');
        }
        return res.data.data;
    }
    catch (e) {
        const msg = e && typeof e === 'object' && 'response' in e
            ? e.response?.data?.message
            : null;
        return rejectWithValue(msg || (e instanceof Error ? e.message : String(e)));
    }
});
export const createReferenceLink = createAsyncThunk('references/createLink', async (body, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.post('/reference-library/link', body);
        if (!res.data?.success) {
            return rejectWithValue(res.data?.message || 'Could not create link');
        }
        return res.data.data;
    }
    catch (e) {
        const msg = e && typeof e === 'object' && 'response' in e
            ? e.response?.data?.message
            : null;
        return rejectWithValue(msg || (e instanceof Error ? e.message : String(e)));
    }
});
export const reportReferenceLibraryItem = createAsyncThunk('references/report', async (input, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.post(`/reference-library/${input.referenceId}/report`, {
            reason: input.reason,
            details: input.details,
        });
        if (!res.data?.success) {
            return rejectWithValue(res.data?.message || 'Report failed');
        }
        return res.data.data;
    }
    catch (e) {
        const msg = e && typeof e === 'object' && 'response' in e
            ? e.response?.data?.message
            : null;
        return rejectWithValue(msg || (e instanceof Error ? e.message : String(e)));
    }
});
const referencesSliceDefinition = createSlice({
    name: 'references',
    initialState,
    reducers: {
        setReferencesSearch(state, action) {
            state.ui.search = action.payload;
        },
        setReferencesTypeFilter(state, action) {
            state.ui.typeFilter = action.payload;
            if (action.payload !== 'all' && action.payload !== 'book') {
                state.ui.genreFilter = 'all';
            }
        },
        setReferencesGenreFilter(state, action) {
            state.ui.genreFilter = action.payload;
        },
        setReferencesBookmarkedOnly(state, action) {
            state.ui.bookmarkedOnly = action.payload;
        },
        clearActiveReference(state) {
            state.activeDetail = null;
            state.detailStatus = 'idle';
            state.detailError = null;
            state.pendingDetailId = null;
        },
        touchReferenceViewerCache(state, action) {
            touchViewerCacheOrder(state, action.payload);
        },
        setReferenceBodyHtml(state, action) {
            state.bodyHtmlByRef[action.payload.referenceId] = action.payload.html;
            state.bodyShaByRef[action.payload.referenceId] = action.payload.bodySha ?? null;
            touchViewerCacheOrder(state, action.payload.referenceId);
        },
        shrinkViewerCache(state, action) {
            evictOldestViewerCacheEntries(state, action.payload);
        },
        evictReferenceViewerCache(state, action) {
            evictViewerCacheEntry(state, action.payload);
        },
        mergeAnnotationFromRealtime(state, action) {
            const refId = action.payload.reference_id;
            if (!refId)
                return;
            const bucket = [...(state.annotationsByRef[refId] ?? [])];
            const i = bucket.findIndex((x) => x.id === action.payload.id);
            if (i >= 0)
                bucket[i] = action.payload;
            else
                bucket.push(action.payload);
            state.annotationsByRef[refId] = bucket.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        },
        mergeCommentFromRealtime(state, action) {
            const refId = action.payload.reference_id;
            if (!refId)
                return;
            const bucket = [...(state.commentsByRef[refId] ?? [])];
            const i = bucket.findIndex((x) => x.id === action.payload.id);
            if (i >= 0)
                bucket[i] = action.payload;
            else
                bucket.push(action.payload);
            state.commentsByRef[refId] = bucket.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        },
        removeAnnotationFromRealtime(state, action) {
            const { referenceId, id } = action.payload;
            state.annotationsByRef[referenceId] = (state.annotationsByRef[referenceId] ?? []).filter((r) => r.id !== id);
        },
        removeCommentFromRealtime(state, action) {
            const { referenceId, id } = action.payload;
            state.commentsByRef[referenceId] = (state.commentsByRef[referenceId] ?? []).filter((r) => r.id !== id);
        },
        /** Optimistic highlight/note row painted before the server insert returns. */
        upsertLocalAnnotation(state, action) {
            const row = action.payload;
            const refId = row.reference_id;
            if (!refId)
                return;
            const bucket = [...(state.annotationsByRef[refId] ?? [])];
            const i = bucket.findIndex((x) => x.id === row.id);
            if (i >= 0)
                bucket[i] = row;
            else
                bucket.push(row);
            state.annotationsByRef[refId] = bucket.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
            // Do NOT touchViewerCacheOrder here — that can evict another book's
            // body_html mid-session and force a heavy reload (UI freeze).
        },
        /** Swap a temp optimistic id for the persisted server row. */
        replaceTempAnnotation(state, action) {
            const { referenceId, tempId, row } = action.payload;
            const bucket = (state.annotationsByRef[referenceId] ?? []).filter((r) => r.id !== tempId);
            if (!bucket.some((x) => x.id === row.id))
                bucket.push(row);
            else {
                const idx = bucket.findIndex((x) => x.id === row.id);
                if (idx >= 0)
                    bucket[idx] = row;
            }
            state.annotationsByRef[referenceId] = bucket.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        },
        clearReferenceViewerSession(state) {
            clearViewerSessionState(state);
        },
    },
    extraReducers(b) {
        // Clear in-memory annotations/comments/body cache on logout so signed-out
        // viewers cannot briefly paint private highlights from a prior session.
        b.addMatcher((action) => action.type === 'auth/logoutUser/fulfilled'
            || action.type === 'auth/logoutUser/rejected', (s) => {
            clearViewerSessionState(s);
        });
        b.addCase(fetchReferenceBookmarks.pending, (s) => {
            s.bookmarksStatus = 'loading';
        });
        b.addCase(fetchReferenceBookmarks.fulfilled, (s, a) => {
            s.bookmarksStatus = 'succeeded';
            s.bookmarkedIds = a.payload;
        });
        b.addCase(fetchReferenceBookmarks.rejected, (s) => {
            s.bookmarksStatus = 'failed';
        });
        b.addCase(toggleReferenceBookmark.fulfilled, (s, a) => {
            const { referenceId, bookmarked } = a.payload;
            if (bookmarked) {
                if (!s.bookmarkedIds.includes(referenceId)) {
                    s.bookmarkedIds = [referenceId, ...s.bookmarkedIds];
                }
            }
            else {
                s.bookmarkedIds = s.bookmarkedIds.filter((id) => id !== referenceId);
            }
        });
        b.addCase(fetchSectionMarks.fulfilled, (s, a) => {
            s.sectionMarksByRef[a.payload.referenceId] = a.payload.rows;
            touchViewerCacheOrder(s, a.payload.referenceId);
        });
        b.addCase(insertSectionMark.fulfilled, (s, a) => {
            const refId = a.payload.reference_id;
            const bucket = [a.payload, ...(s.sectionMarksByRef[refId] ?? [])];
            s.sectionMarksByRef[refId] = bucket;
        });
        b.addCase(deleteSectionMark.fulfilled, (s, a) => {
            const { id, referenceId } = a.payload;
            s.sectionMarksByRef[referenceId] = (s.sectionMarksByRef[referenceId] ?? []).filter((m) => m.id !== id);
        });
        b.addCase(fetchReferencesLibrary.pending, (s, a) => {
            const append = !!a.meta.arg?.append;
            s.libraryError = null;
            if (!append) {
                s.libraryStatus = 'loading';
            }
        });
        b.addCase(fetchReferencesLibrary.fulfilled, (s, a) => {
            const arg = a.meta.arg ?? {};
            const append = !!arg.append;
            const lim = arg.limit ?? 40;
            const q = arg.search?.trim();
            const searching = !!(q && q.length >= 2);
            s.libraryStatus = 'succeeded';
            const rows = a.payload;
            if (append) {
                const seen = new Set(s.library.map((r) => r.id));
                const next = [...s.library];
                for (const r of rows) {
                    if (!seen.has(r.id)) {
                        next.push(r);
                        seen.add(r.id);
                    }
                }
                s.library = next;
            }
            else {
                s.library = rows;
            }
            if (searching) {
                s.libraryHasMore = false;
            }
            else {
                s.libraryHasMore = rows.length >= lim;
            }
        });
        b.addCase(fetchReferencesLibrary.rejected, (s, a) => {
            const append = !!a.meta.arg?.append;
            if (!append) {
                s.libraryStatus = 'failed';
                s.libraryHasMore = false;
            }
            s.libraryError =
                a.payload ??
                    (typeof a.error.message === 'string' ? a.error.message : null) ??
                    'Failed to load references';
        });
        b.addCase(fetchReferenceDetail.pending, (s, a) => {
            s.detailStatus = 'loading';
            s.detailError = null;
            // Keep the current row when re-fetching the same id so lesson embeds
            // (slide decks, quizzes) do not flash empty / return null mid-load.
            if (s.activeDetail?.id !== a.meta.arg) {
                s.activeDetail = null;
            }
            s.pendingDetailId = a.meta.arg;
        });
        b.addCase(fetchReferenceDetail.fulfilled, (s, a) => {
            if (a.meta.arg !== s.pendingDetailId)
                return;
            s.detailStatus = 'succeeded';
            const row = a.payload ?? null;
            const freshSha = row?.body_sha256 ?? null;
            if (row?.id && freshSha && s.bodyHtmlByRef[row.id] && s.bodyShaByRef[row.id] !== freshSha) {
                // The body was re-converted/re-published since this HTML was cached —
                // drop the stale entry so the body loader refetches the new bytes.
                delete s.bodyHtmlByRef[row.id];
                delete s.bodyShaByRef[row.id];
                releaseBodyHtml(row.id);
            }
            if (row?.id && row.body_html?.trim()) {
                s.bodyHtmlByRef[row.id] = row.body_html;
                s.bodyShaByRef[row.id] = freshSha;
            }
            s.activeDetail = row ? { ...row, body_html: null } : null;
            s.pendingDetailId = null;
            if (row?.id) {
                touchViewerCacheOrder(s, row.id);
            }
        });
        b.addCase(fetchReferenceDetail.rejected, (s, a) => {
            if (a.meta.arg !== s.pendingDetailId)
                return;
            s.detailStatus = 'failed';
            s.detailError = a.payload ?? a.error.message ?? 'Failed to load reference';
            s.pendingDetailId = null;
        });
        b.addCase(fetchAnnotationsForReference.pending, (s, a) => {
            const referenceId = typeof a.meta.arg === 'string' ? a.meta.arg : a.meta.arg.referenceId;
            s.annotationsFetchGenByRef[referenceId] = (s.annotationsFetchGenByRef[referenceId] ?? 0) + 1;
        });
        b.addCase(fetchAnnotationsForReference.fulfilled, (s, a) => {
            const { referenceId, rows, requestedAt, fetchGen } = a.payload;
            const currentGen = s.annotationsFetchGenByRef[referenceId] ?? 0;
            if (fetchGen !== currentGen) {
                // Newer fetch already in flight or completed — drop this stale list.
                return;
            }
            const existing = s.annotationsByRef[referenceId] ?? [];
            s.annotationsByRef[referenceId] = mergeFetchedAnnotations(existing, rows, requestedAt);
            touchViewerCacheOrder(s, referenceId);
        });
        b.addCase(fetchCommentsForReference.fulfilled, (s, a) => {
            s.commentsByRef[a.payload.referenceId] = a.payload.rows;
            touchViewerCacheOrder(s, a.payload.referenceId);
        });
        b.addCase(insertAnnotation.fulfilled, (s, a) => {
            const { row, clientTempId } = a.payload;
            const refId = row.reference_id;
            let bucket = [...(s.annotationsByRef[refId] ?? [])];
            if (clientTempId) {
                bucket = bucket.filter((x) => x.id !== clientTempId);
            }
            if (!bucket.some((x) => x.id === row.id))
                bucket.push(row);
            s.annotationsByRef[refId] = bucket;
        });
        b.addCase(updateAnnotationNote.fulfilled, (s, a) => {
            const refId = a.payload.reference_id;
            const bucket = [...(s.annotationsByRef[refId] ?? [])];
            const idx = bucket.findIndex((x) => x.id === a.payload.id);
            if (idx >= 0)
                bucket[idx] = a.payload;
            else
                bucket.push(a.payload);
            s.annotationsByRef[refId] = bucket;
        });
        b.addCase(updateAnnotationVisibility.fulfilled, (s, a) => {
            const refId = a.payload.reference_id;
            const bucket = [...(s.annotationsByRef[refId] ?? [])];
            const idx = bucket.findIndex((x) => x.id === a.payload.id);
            if (idx >= 0)
                bucket[idx] = a.payload;
            else
                bucket.push(a.payload);
            s.annotationsByRef[refId] = bucket;
        });
        b.addCase(deleteAnnotation.fulfilled, (s, a) => {
            const { id, referenceId } = a.payload;
            s.annotationsByRef[referenceId] = (s.annotationsByRef[referenceId] ?? []).filter((r) => r.id !== id);
        });
        b.addCase(insertComment.fulfilled, (s, a) => {
            const refId = a.payload.reference_id;
            const bucket = [...(s.commentsByRef[refId] ?? [])];
            if (!bucket.some((x) => x.id === a.payload.id))
                bucket.push(a.payload);
            s.commentsByRef[refId] = bucket;
        });
        b.addCase(updateComment.fulfilled, (s, a) => {
            const { referenceId, row } = a.payload;
            if (!row)
                return;
            const bucket = [...(s.commentsByRef[referenceId] ?? [])];
            const idx = bucket.findIndex((x) => x.id === row.id);
            if (idx >= 0)
                bucket[idx] = row;
            s.commentsByRef[referenceId] = bucket.sort((x, y) => new Date(x.created_at).getTime() - new Date(y.created_at).getTime());
        });
        b.addCase(insertReferenceRow.fulfilled, (s, a) => {
            if (!a.payload)
                return;
            if (a.payload.status !== 'active')
                return;
            const rest = s.library.filter((x) => x.id !== a.payload.id);
            s.library = [a.payload, ...rest];
        });
        b.addCase(finalizeReferenceUpload.fulfilled, (s, a) => {
            if (!a.payload)
                return;
            if (a.payload.status !== 'active')
                return;
            const rest = s.library.filter((x) => x.id !== a.payload.id);
            s.library = [a.payload, ...rest];
            if (s.activeDetail?.id === a.payload.id)
                s.activeDetail = a.payload;
        });
        b.addCase(createReferenceLink.fulfilled, (s, a) => {
            if (!a.payload)
                return;
            if (a.payload.status !== 'active')
                return;
            const rest = s.library.filter((x) => x.id !== a.payload.id);
            s.library = [a.payload, ...rest];
        });
        b.addCase(reportReferenceLibraryItem.fulfilled, (s, a) => {
            const refId = a.meta.arg.referenceId;
            if (s.activeDetail?.id === refId) {
                s.activeDetail = { ...s.activeDetail, moderation_status: 'flagged' };
            }
            const i = s.library.findIndex((r) => r.id === refId);
            if (i >= 0) {
                const row = s.library[i];
                s.library = [
                    ...s.library.slice(0, i),
                    { ...row, moderation_status: 'flagged' },
                    ...s.library.slice(i + 1),
                ];
            }
        });
    },
});
export const { setReferencesSearch, setReferencesTypeFilter, setReferencesGenreFilter, setReferencesBookmarkedOnly, clearActiveReference, touchReferenceViewerCache, setReferenceBodyHtml, shrinkViewerCache, evictReferenceViewerCache, mergeAnnotationFromRealtime, mergeCommentFromRealtime, removeAnnotationFromRealtime, removeCommentFromRealtime, upsertLocalAnnotation, replaceTempAnnotation, clearReferenceViewerSession, } = referencesSliceDefinition.actions;
export default referencesSliceDefinition.reducer;
